import { createContext, useContext, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

export interface MenuItem {
  label: string;
  action: (event: MouseEvent) => void;
}

type ItemCallback = (event: MouseEvent) => MenuItem | MenuItem[];

interface ContextMenuState {
  useItems: (cb: ItemCallback) => void;
  registerOnce: (cb: ItemCallback | MenuItem | MenuItem[]) => void;
  evaluateItems: (event: MouseEvent) => MenuItem[];
  clearOneShot: () => void;
}

const ContextMenuContext = createContext<ContextMenuState | null>(null);
ContextMenuContext.displayName = 'ContextMenu';

export function ContextMenuProvider({ children }: { children: ReactNode }) {
  const itemCallbacks = useRef(new Set<ItemCallback>());
  const oneShotCallbacks = useRef(new Set<ItemCallback>());

  function evaluateItems(event: MouseEvent): MenuItem[] {
    const actions: MenuItem[] = [];

    function evalCb(cb: ItemCallback) {
      const items = cb(event);
      if (Array.isArray(items)) {
        actions.push(...items);
      } else {
        actions.push(items);
      }
    }
    itemCallbacks.current.forEach(evalCb);
    oneShotCallbacks.current.forEach(evalCb);
    return actions;
  }

  function clearOneShot() {
    oneShotCallbacks.current.clear();
  }

  function useItems(cb: ItemCallback) {
    const cbRef = useRef(cb);
    cbRef.current = cb;
    useEffect(() => {
      const fn: ItemCallback = (e) => cbRef.current(e);
      itemCallbacks.current.add(fn);
      return () => {
        itemCallbacks.current.delete(fn);
      };
    }, []);
  }

  function registerOnce(cb: ItemCallback | MenuItem | MenuItem[]) {
    if (typeof cb === 'function') {
      oneShotCallbacks.current.add(cb);
    } else {
      oneShotCallbacks.current.add(() => cb);
    }
  }

  const value: ContextMenuState = {
    useItems,
    registerOnce,
    evaluateItems,
    clearOneShot
  };

  return (
    <ContextMenuContext.Provider value={value}>
      {children}
    </ContextMenuContext.Provider>
  );
}

export function useContextMenu(): ContextMenuState {
  const ctx = useContext(ContextMenuContext);
  if (!ctx) {
    throw new Error('ContextMenu not provided');
  }
  return ctx;
}

/**
 * Hook for registering context menu items from a component.
 * The items are registered while the component is mounted.
 */
export function useContextMenuItems(cb: ItemCallback) {
  const menuSystem = useContextMenu();
  menuSystem.useItems(cb);
}
